import React, { Fragment, useEffect, useMemo, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XMarkIcon, TrashIcon } from '@heroicons/react/24/outline'
import request from '../utils/request'

// Centralized, scannable review step for the "mark items for refund" flow.
// The existing per-product refund logic (marking + approval queue) stays in
// place. This modal just lets an operator:
//   1. Re-check which items are actually being refunded
//   2. See the exact guest email + total that will be sent
//   3. Send it via the new admin-only /orders/:oid/refund-email endpoint
function formatCurrency(n) {
  const value = Number(n) || 0
  return value.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
}

function productLineTotal(p) {
  const price = Number(p?.price) || 0
  const qty = Number(p?.quantity) || 1
  return price * qty
}

export default function RefundReviewModal({ open, onClose, order, items = [], onSent }) {
  const [excluded, setExcluded] = useState(() => new Set())
  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  useEffect(() => {
    if (open) {
      setExcluded(new Set())
      setError('')
      setSuccess('')
    }
  }, [open])

  const selected = useMemo(
    () => items.filter((it) => !excluded.has(`${it.orderId}:${it.productIndex}`)),
    [items, excluded]
  )

  const total = useMemo(
    () => selected.reduce((sum, it) => sum + productLineTotal(it.product), 0),
    [selected]
  )

  const toggleExclude = (key) => {
    setExcluded((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const canSend = selected.length > 0 && order?._id && order?.guestEmail

  const guestName = order?.guestName || order?.guestFirstName || 'there'

  const handleSend = async () => {
    if (!canSend) return
    setSending(true)
    setError('')
    setSuccess('')
    try {
      const payload = {
        amount: total,
        products: selected.map((it) => ({
          name: it.product?.name || it.product?.title,
          price: Number(it.product?.price) || 0,
          quantity: Number(it.product?.quantity) || 1,
          imageUrl: it.product?.imageUrl,
        })),
      }
      await request.post(`/orders/${order._id}/refund-email`, payload, {
        withCredentials: true,
      })
      setSuccess(`Refund email sent to ${order.guestEmail}.`)
      setTimeout(() => {
        onSent?.()
      }, 400)
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to send refund email')
    } finally {
      setSending(false)
    }
  }

  return (
    <Transition.Root show={!!open} as={Fragment}>
      <Dialog as="div" className="relative z-[70]" onClose={() => onClose?.()}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-zinc-900/50" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 sm:p-6">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-2 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-2 sm:scale-95"
            >
              <Dialog.Panel className="relative flex w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
                <header className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
                  <div>
                    <Dialog.Title className="text-lg font-semibold text-gray-900">
                      Review refund email
                    </Dialog.Title>
                    <p className="text-xs text-gray-500">
                      Order #{order?.uniqueId || '—'} · {order?.guestEmail || 'no guest email'}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => onClose?.()}
                    className="rounded-md p-1 text-gray-500 hover:bg-gray-100"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </header>

                <div className="grid min-h-0 grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
                  {/* ITEMS */}
                  <section className="border-b border-gray-200 p-5 lg:border-b-0 lg:border-r">
                    <h3 className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                      Items to refund ({selected.length})
                    </h3>
                    {items.length === 0 ? (
                      <p className="mt-3 text-sm text-gray-500">
                        No items are marked for refund yet.
                      </p>
                    ) : (
                      <ul className="mt-3 space-y-2">
                        {items.map((it) => {
                          const key = `${it.orderId}:${it.productIndex}`
                          const excludedHere = excluded.has(key)
                          return (
                            <li
                              key={key}
                              className={`flex items-center justify-between gap-3 rounded-xl border px-3 py-2 ${
                                excludedHere ? 'border-gray-200 bg-gray-50 opacity-60' : 'border-red-200 bg-red-50/50'
                              }`}
                            >
                              <div className="flex min-w-0 items-center gap-3">
                                {it.product?.imageUrl ? (
                                  <img
                                    src={it.product.imageUrl}
                                    alt=""
                                    className="h-10 w-10 flex-shrink-0 rounded-lg object-cover"
                                  />
                                ) : (
                                  <div className="h-10 w-10 flex-shrink-0 rounded-lg bg-gray-100" />
                                )}
                                <div className="min-w-0">
                                  <p className={`truncate text-sm font-medium ${excludedHere ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                                    {it.product?.name || it.product?.title || 'Item'}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    #{it.orderUniqueId} · qty {it.product?.quantity || 1} ·{' '}
                                    {formatCurrency(productLineTotal(it.product))}
                                  </p>
                                  {it.product?.refundReason ? (
                                    <p className="text-[11px] text-red-700">
                                      Reason: {it.product.refundReason}
                                    </p>
                                  ) : null}
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => toggleExclude(key)}
                                className="rounded-md border border-gray-200 bg-white p-1.5 text-gray-500 hover:bg-gray-50"
                                title={excludedHere ? 'Include again' : 'Exclude from this email'}
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </li>
                          )
                        })}
                      </ul>
                    )}

                    <div className="mt-4 flex items-center justify-between rounded-xl bg-gray-50 px-4 py-3">
                      <span className="text-sm font-medium text-gray-700">Total refund</span>
                      <span className="text-lg font-semibold text-red-700">
                        {formatCurrency(total)}
                      </span>
                    </div>
                  </section>

                  {/* EMAIL PREVIEW */}
                  <section className="p-5">
                    <h3 className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                      Email preview
                    </h3>
                    <div className="mt-3 overflow-hidden rounded-xl border border-gray-200 bg-white">
                      <div className="border-b border-gray-100 px-4 py-3 text-xs text-gray-600">
                        <div>
                          <span className="font-medium text-gray-500">To:</span>{' '}
                          <span className="text-gray-900">{order?.guestEmail || '—'}</span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-500">Subject:</span>{' '}
                          <span className="text-gray-900">
                            Your Bellhopt order #{order?.uniqueId} — {selected.length}{' '}
                            item{selected.length !== 1 ? 's' : ''} refunded
                          </span>
                        </div>
                      </div>
                      <div className="space-y-3 px-4 py-4 text-sm text-gray-800">
                        <p>Hi {guestName},</p>
                        <p>
                          We wanted to let you know that the following item
                          {selected.length !== 1 ? 's from your order have ' : ' from your order has '}
                          been refunded:
                        </p>
                        <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
                          {selected.map((it) => {
                            const key = `${it.orderId}:${it.productIndex}`
                            return (
                              <li key={key} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                                <span className="truncate text-gray-900">
                                  {it.product?.name || it.product?.title || 'Item'}
                                  <span className="ml-1 text-xs text-gray-500">× {it.product?.quantity || 1}</span>
                                </span>
                                <span className="font-medium text-gray-900">
                                  {formatCurrency(productLineTotal(it.product))}
                                </span>
                              </li>
                            )
                          })}
                        </ul>
                        <div className="flex items-center justify-between rounded-lg bg-red-50 px-3 py-2 text-sm">
                          <span className="font-medium text-red-700">Total refunded</span>
                          <span className="font-semibold text-red-700">{formatCurrency(total)}</span>
                        </div>
                        <p>
                          The refund should land in your original payment method within
                          5–10 business days. If you have any questions, just reply to
                          this email.
                        </p>
                        <p>— The Bellhopt team</p>
                      </div>
                    </div>

                    {error ? (
                      <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                        {error}
                      </div>
                    ) : null}
                    {success ? (
                      <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
                        {success}
                      </div>
                    ) : null}
                  </section>
                </div>

                <footer className="flex items-center justify-between gap-3 border-t border-gray-200 bg-gray-50 px-5 py-4">
                  <p className="text-xs text-gray-500">
                    This will reuse the existing guest refund template and send from
                    support. It does not change refund approval state on the order.
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => onClose?.()}
                      className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleSend}
                      disabled={!canSend || sending}
                      className="rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-gray-300"
                    >
                      {sending ? 'Sending…' : `Send refund email · ${formatCurrency(total)}`}
                    </button>
                  </div>
                </footer>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}
